import { Consumer, Subscription } from "@rails/actioncable"
import { AppContext } from "Shared/AppContext"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useListModeratedStudyChatMessages } from "~/api/generated/usabilityhub-components"
import { ChatMessage } from "~/api/generated/usabilityhubSchemas"

export const useChatWebhook = (
  channelName: string,
  moderatedStudyApplicationId: string
) => {
  const { consumer } = useContext(AppContext)
  const channel = useRef<Subscription<Consumer>>()

  const {
    data: initialMessages,
    isLoading,
    isError,
  } = useListModeratedStudyChatMessages({
    queryParams: {
      moderated_study_application_id: moderatedStudyApplicationId,
    },
  })

  const [status, setStatus] = useState<"loading" | "ready" | "disconnected">(
    "loading"
  )
  const [messages, setMessages] = useState<ChatMessage[]>([])

  // Whenever we get a fresh master list of chat history from the server,
  // blat it over top of whatever local state we had before.
  useEffect(() => {
    if (initialMessages) {
      setMessages(initialMessages)
    }
  }, [initialMessages])

  const sendMessage = useCallback((text: string) => {
    const message = { action: "message", text }
    channel.current?.perform("send_message", message)
  }, [])

  // Currently only exists on the researcher channel
  const markAsRead = useCallback(() => {
    channel.current?.perform("mark_as_read", { action: "mark_as_read" })
  }, [])

  useEffect(() => {
    // Don't set up the channel until the initial API request completes
    if (isLoading || isError) return

    channel.current = consumer?.subscriptions.create(
      {
        channel: channelName,
        moderated_study_application_id: moderatedStudyApplicationId,
      },
      {
        connected: () => {
          setStatus("ready")
        },
        disconnected: () => {
          setStatus("disconnected")
        },
        received: (message: ChatMessage) => {
          setMessages((messages) => [...messages, message])
        },
      }
    )
    return () => {
      if (channel.current) {
        channel.current.unsubscribe()
        channel.current = undefined
      }
    }
  }, [consumer, isLoading, isError, moderatedStudyApplicationId])

  // Populate the array of messages from the API response
  useEffect(() => {
    if (initialMessages && messages.length === 0) {
      setMessages(initialMessages)
    }
  }, [initialMessages])

  return {
    status,
    messages,
    sendMessage,
    markAsRead,
  }
}
